<template>
  <div
    class="modal fade"
    id="usedModesModal"
    tabindex="-1"
    aria-hidden="true"
    ref="modalRef"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content used-modes-modal">
        <!-- Header -->
        <div class="modal-header">
          <h5 class="modal-title">
            {{ $t("qbank.new_test.previously_answered") }}
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <div v-if="usedModes.length === 0" class="empty-state">
            <i class="fas fa-inbox"></i>
            <p>{{ $t("qbank.new_test.no_used_questions") }}</p>
          </div>

          <div v-else class="modes-list">
            <!-- Unused (New Questions) Option -->
            <div
              class="mode-item"
              :class="{ highlighted: localUnusedSelected }"
              @click="toggleUnused"
            >
              <div class="mode-checkbox">
                <input
                  type="checkbox"
                  :checked="localUnusedSelected"
                  @click.stop="toggleUnused"
                />
              </div>
              <div class="mode-info">
                <span class="mode-name">{{ getModeName(unusedMode.key) }}</span>
                <span class="mode-count"
                  >{{ unusedMode.count }} {{ $t("qbank.questions") }}</span
                >
              </div>
            </div>

            <!-- Divider -->
            <div class="modes-divider"></div>

            <!-- Used Modes -->
            <div
              v-for="mode in usedModes"
              :key="mode.key"
              class="mode-item"
              @click="toggleMode(mode.key)"
            >
              <div class="mode-checkbox">
                <input
                  type="checkbox"
                  :checked="localSelectedModes.has(mode.key)"
                  @click.stop="toggleMode(mode.key)"
                />
              </div>
              <div class="mode-info">
                <span class="mode-name">{{ getModeName(mode.key) }}</span>
                <span class="mode-count"
                  >{{ mode.count }} {{ $t("qbank.questions") }}</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-confirm"
            data-bs-dismiss="modal"
            @click="confirmSelection"
          >
            {{ $t("common.save") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "UsedModesModal",
  props: {
    usedModes: {
      type: Array,
      default: () => [],
    },
    unusedMode: {
      type: Object,
      default: () => ({ key: "unused", name: "Unused", count: 0 }),
    },
    selectedModes: {
      type: Array,
      default: () => [],
    },
    unusedSelected: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:selected-modes", "update:unused-selected"],
  data() {
    return {
      localSelectedModes: new Set(),
      localUnusedSelected: false,
      modalInstance: null,
    };
  },
  watch: {
    selectedModes: {
      immediate: true,
      handler(newVal) {
        this.localSelectedModes = new Set(newVal);
      },
    },
    unusedSelected: {
      immediate: true,
      handler(newVal) {
        this.localUnusedSelected = newVal;
      },
    },
  },
  methods: {
    show() {
      this.localSelectedModes = new Set(this.selectedModes);
      this.localUnusedSelected = this.unusedSelected;

      if (!this.modalInstance) {
        this.modalInstance = new bootstrap.Modal(this.$refs.modalRef);
      }
      this.modalInstance.show();
    },
    getModeName(key) {
      const modeNames = {
        unused: this.$t("qbank.type.unused"),
        correct: this.$t("qbank.type.correct"),
        incorrect: this.$t("qbank.type.incorrect"),
        omitted: this.$t("qbank.new_test.omitted"),
        marked: this.$t("qbank.type.marked"),
        used: this.$t("qbank.type.used"),
      };
      return modeNames[key] || key;
    },
    toggleUnused() {
      this.localUnusedSelected = !this.localUnusedSelected;
    },
    toggleMode(key) {
      if (this.localSelectedModes.has(key)) {
        this.localSelectedModes.delete(key);
      } else {
        this.localSelectedModes.add(key);
      }
      // Force reactivity
      this.localSelectedModes = new Set(this.localSelectedModes);
    },
    confirmSelection() {
      this.$emit("update:selected-modes", [...this.localSelectedModes]);
      this.$emit("update:unused-selected", this.localUnusedSelected);
    },
  },
};
</script>

<style scoped>
.used-modes-modal {
  border-radius: 20px;
  border: none;
  overflow: hidden;
}

.modal-header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  padding: 20px 24px;
}

.modal-title {
  font-weight: 700;
  font-size: 1.1rem;
}

.modal-header .btn-close {
  filter: brightness(0) invert(1);
  opacity: 0.8;
}

.modal-header .btn-close:hover {
  opacity: 1;
}

.modal-body {
  padding: 20px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #94a3b8;
}

.empty-state i {
  font-size: 40px;
  margin-bottom: 12px;
}

.modes-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.modes-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
  margin: 6px 0;
}

.mode-item {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.mode-item:hover {
  border-color: #00c744;
  background: #f0fdf4;
}

.mode-item.highlighted {
  border-color: #00c744;
  background: #f0fdf4;
}

.mode-checkbox {
  margin-right: 14px;
}

.mode-checkbox input {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #00c744;
}

.mode-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.mode-name {
  font-weight: 600;
  color: #2d3748;
  font-size: 0.95rem;
}

.mode-count {
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 2px;
}

.modal-footer {
  border: none;
  padding: 16px 24px;
  background: #f8fafc;
}

.btn-confirm {
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 6px 20px rgba(0, 199, 68, 0.3);
}

.btn-confirm:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.4);
  color: white;
}

/* RTL Support */
[dir="rtl"] .mode-checkbox {
  margin-right: 0;
  margin-left: 14px;
}

/* Dark Mode */
[data-bs-theme="dark"] .used-modes-modal,
.dark .used-modes-modal {
  background: #1e293b;
}

[data-bs-theme="dark"] .mode-item,
.dark .mode-item {
  background: #334155;
  border-color: #475569;
}

[data-bs-theme="dark"] .mode-item:hover,
.dark .mode-item:hover {
  background: rgba(0, 199, 68, 0.1);
}

[data-bs-theme="dark"] .mode-item.highlighted,
.dark .mode-item.highlighted {
  background: rgba(0, 199, 68, 0.1);
  border-color: #00c744;
}

[data-bs-theme="dark"] .modes-divider,
.dark .modes-divider {
  background: linear-gradient(90deg, transparent, #475569, transparent);
}

[data-bs-theme="dark"] .mode-name,
.dark .mode-name {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .mode-count,
.dark .mode-count {
  color: #94a3b8;
}

[data-bs-theme="dark"] .modal-footer,
.dark .modal-footer {
  background: #1e293b;
}
</style>
